import { ChecklistTask } from "./ChecklistTask";
import { MarinerProfile } from "./MarinerProfile";
import { TravelDocument } from "./TravelDocument";
import { User } from "./User";
import { Vessel } from "./Vessel";

export type JobOrder = {
  id?: number;
  createdDate: string; // string type to bind directly to date input
  fillByDate: string;
  smis: string;
  vessel: Vessel | undefined;
  assignee?: User;
  assigneeId?: string;
  onsigner: MarinerProfile | undefined;
  offsigner: MarinerProfile | undefined;
  onsignerOnboarded: boolean | undefined;
  onsignerLeavingFrom: string | undefined;
  onsignerDestination: string | undefined;
  onsignerTravelTimestamp: string | undefined; // string to bind directly to time input element
  onsignerAccomodation: string | undefined;
  onsignerPickupDate: string | undefined;
  onsignerDropoffDate: string | undefined;
  onsignerNotes: string | undefined;
  onsignerUnionHall: string | undefined;
  offsignerDeboarded: boolean | undefined;
  offsignerLeavingFrom: string | undefined;
  offsignerDestination: string | undefined;
  offsignerTravelTimestamp: string | undefined; // string to bind directly to input element
  offsignerAccomodation: string | undefined;
  offsignerPickupDate: string | undefined;
  offsignerDropoffDate: string | undefined;
  offsignerNotes: string | undefined;
  checklistRoot: ChecklistTask | undefined;
  travelDocuments: TravelDocument[];
  rating: JobRating;
  port: string | undefined;
  reliefReason: ReliefReason | undefined;
  flightNotice: boolean | undefined;
  reliefTime: number | undefined;
  covid19Testing: string | undefined;
  testingLocation: string | undefined;
  jobType: LicensedJobType | UnLicensedJobType | undefined;
  publish: Publish | undefined;
  deleted: boolean;
  note: string | undefined;
  unopenedJobOrder?: boolean;
  modifiedByCaptain?: boolean;
  extra: boolean;
  lastUpdated?: string;
};

export type JobOrderMetadata = {
  activeCount: number;
  criticalCount: number;
  otherCount: number;
  monthCompletedCount: number;
  monthArchivedCount: number;
};

export type JobOrderExecView = {
  billetsToBeFilled: number;
  billetsFilled: number;
  nonUnionMariners: NonUnionMarinersByRating[];
  gappedBilletsByRating: {
    vesselId: number;
    vessel: string;
    rating: JobRating;
    gappedBillets: number;
  }[];
  vesselHeadCounts: HeadCountsByVessel[];
  gappedAndFilledBillets: GappedAndFilledBillet[];
  upcomingExpiringPermanentOfficerDocuments: ExpiredOfficerDocuments[];
};

export type ExpiredOfficerDocuments = {
  vesselName: string;
  marinerName: string;
  documentName: string;
  expirationDate: string;
  fileKey: string;
  documentType: string;
  documentCategory: string;
  contentType: string;
}

export type NonUnionMarinersByRating = {
  vesselId: number;
  vessel: string;
  rating: JobRating;
  nonUnionMariners: number;
};

export type HeadCountsByVessel = {
  vesselId: number;
  vessel: string;
  headCount: number;
  rosFos: string;
};

export type GappedAndFilledBillet = {
  fleetName: string;
  vesselName: string;
  assigneeId: string;
  jobType: string;
  rating: JobRating;
  createdDate: string;
  fillByDate: string;
  completedDate: string;
  departedDate: string | null;
  daysGapped: number;
  billetStatus: string;
}

export type JobOrderDuplicate = {
  id: number;
  fillByDate: string;
  port: string;
  vesselName: string;
  rating: string;
  offsignerId: number;
  offsignerFirstName: string;
  offsignerLastName: string;
  onsignerId: number;
  onsignerFirstName: string;
  onsignerLastName: string;
};

export type VesselJobOrderAggregate = {
  vesselId: number;
  nonCriticalJobRatings: {
    vesselId: number;
    ratingCount: number;
    jobRating: JobRating;
  }[];
  criticalJobRatings: {
    vesselId: number;
    ratingCount: number;
    jobRating: JobRating;
  }[];
  otherJobRatings: {
    vesselId: number;
    ratingCount: number;
    jobRating: JobRating;
  }[];
};

export enum JobRating {
  _Master = "_Master",
  _CM = "_CM",
  _2M = "_2M",
  _3M = "_3M",
  _CE = "_CE",
  _1AE = "_1AE",
  _2AE = "_2AE",
  _3AE = "_3AE",
  _PE = "_PE",
  _PIL = "_PIL",
  _REO = "_REO",
  _ETO = "_ETO",
  _CHC = "_CHC",
  _2CB = "_2CB",
  _AC = "_AC",
  _BSN = "_BSN",
  _DECKCADET = "_DECKCADET",
  _ENGINECADET = "_ENGINECADET",
  _GUDE = "_GUDE",
  _GUDEM1D = "_GUDEM1D",
  _GUDEM1E = "_GUDEM1E",
  _MDR = "_MDR",
  _OIL = "_OIL",
  _OMU = "_OMU",
  _ABB = "_ABB",
  _ABM = "_ABM",
  _ABG = "_ABG",
  _ABS = "_ABS",
  _AB = "_AB",
  _OS = "_OS",
  _QMED = "_QMED",
  _QMEDDAY = "_QMEDDAY",
  _QMEDE = "_QMEDE",
  _SA = "_SA",
  _STB = "_STB",
  _STW = "_STW",
  _STKP = "_STKP",
  _UNLICENSEDAPPRENTICE = "_UNLICENSEDAPPRENTICE",
  _WPR = "_WPR",
  _STOS = "_STOS",
  _OL = "_OL",
  _GVA = "_GVA",
  _CC = "_CC",
  _2CC = "_2CC",
  _PM = "_PM",
  _PRE = "_PRE",
  _SBY = "_SBY",
  _TAPP = "_TAPP",
  _JM = "_JM",
  _ABT = "_ABT",
  _AE = "_AE",
  _GU = "_GU",
  _QEE = "_QEE",
  _OSA2 = "_OSA2",
  _OSA3 = "_OSA3",
  _WA2 = "_WA2",
  _WA3 = "_WA3",
  _PTE = "_PTE",
  _ABTA = "_ABTA",
  _LEADLNGT = "_LEADLNGT",
  _LNGT = "_LNGT",
  _LNGTA = "_LNGTA",
  _LNGBGU = "_LNGBGU"
}

export const DisabledJobRatings: Set<JobRating> = new Set([
  // QMED Ratings
  JobRating._QMEDDAY,
  JobRating._QMEDE,
  // GUDE Ratings
  JobRating._GUDEM1D,
  JobRating._GUDEM1E,
  // UNLICENSEDAPPRENTICE Rating
  JobRating._UNLICENSEDAPPRENTICE,
]);

export const AB_VARIANT_RATINGS: Set<JobRating> = new Set([
  JobRating._AB,
  JobRating._ABB,
  JobRating._ABM,
  JobRating._ABG,
  JobRating._ABS,
]);

export const JobRatingLabel: { [key in JobRating]: string } = {
  [JobRating._Master]: "Master",
  [JobRating._CM]: "Chief Mate",
  [JobRating._2M]: "2nd Mate",
  [JobRating._3M]: "3rd Mate",
  [JobRating._JM]: "Junior Mate",
  [JobRating._CE]: "Chief Engineer",
  [JobRating._1AE]: "1st Assistant Engineer",
  [JobRating._2AE]: "2nd Assistant Engineer",
  [JobRating._3AE]: "3rd Assistant Engineer",
  [JobRating._AE]: "Assistant Engineer",
  [JobRating._PE]: "Pwr Elect",
  [JobRating._PIL]: "Alaska Pilot",
  [JobRating._REO]: "Radio Electronics Officer",
  [JobRating._ETO]: "Electronics Technician Officer",
  [JobRating._CHC]: "Chief Cook",
  [JobRating._2CB]: "2nd Cook / Baker",
  [JobRating._AC]: "Assistant Cook",
  [JobRating._BSN]: "Bosun",
  [JobRating._DECKCADET]: "Deck Cadet",
  [JobRating._ENGINECADET]: "Engine Cadet",
  [JobRating._TAPP]: "Technical Apprentice",
  [JobRating._GUDE]: "GUDE",
  [JobRating._GUDEM1D]: "GUDE (D)",
  [JobRating._GUDEM1E]: "GUDE (E)",
  [JobRating._MDR]: "MDR/ Nurse",
  [JobRating._OIL]: "Oiler",
  [JobRating._OMU]: "OMU",
  [JobRating._ABB]: "Able Bodied Seaman Blue/Limited",
  [JobRating._ABM]: "Able Bodied Seaman Maintenance",
  [JobRating._ABG]: "Able Bodied Seaman Green/Unlimited",
  [JobRating._ABS]: "Able Seaman Special",
  [JobRating._ABT]: "Able Bodied Seaman Tankerman",
  [JobRating._AB]: "Able Bodied Seaman",
  [JobRating._OS]: "Ordinary Seaman",
  [JobRating._QMED]: "QMED",
  [JobRating._QMEDDAY]: "QMED (DAY)",
  [JobRating._QMEDE]: "QMED (ELEC)",
  [JobRating._SA]: "Steward Assistant",
  [JobRating._STB]: "Steward Baker",
  [JobRating._STW]: "Steward",
  [JobRating._STKP]: "Store Keeper",
  [JobRating._UNLICENSEDAPPRENTICE]: "Unlicensed Apprentice",
  [JobRating._WPR]: "Wiper",
  [JobRating._STOS]: "Specially Trained Ordinary Seaman",
  [JobRating._OL]: "OL",
  [JobRating._GVA]: "GVa",
  [JobRating._CC]: "CC",
  [JobRating._2CC]: "2CC",
  [JobRating._PM]: "Port Mate",
  [JobRating._PRE]: "Port Relief Engineer",
  [JobRating._SBY]: "Standby",
  [JobRating._GU]: "General Utility",
  [JobRating._QEE]: "QEE",
  [JobRating._OSA2]: "OSA2",
  [JobRating._OSA3]: "OSA3",
  [JobRating._WA2]: "WA2",
  [JobRating._WA3]: "WA3",
  [JobRating._PTE]: "Port Engineer",
  [JobRating._ABTA]: "AB Tankerman Assistant",
  [JobRating._LEADLNGT]: "Lead LNG Tankerman PIC",
  [JobRating._LNGT]: "LNG Tankerman PIC",
  [JobRating._LNGTA]: "LNG Tankerman Assistant PIC",
  [JobRating._LNGBGU]: "LNG Barge General Utility",
};

// enum for Relief Reason field
export enum ReliefReason {
  VACATION = "VACATION",
  END_OF_TOUR = "END_OF_TOUR",
  MUTUAL_CONSENT = "MUTUAL_CONSENT",
  QUIT = "QUIT",
  RELIEF_60_DAY = "RELIEF_60_DAY",
  RELIEF_45_DAY = "RELIEF_45_DAY",
  RELIEF_COMPLETED = "RELIEF_COMPLETED",
  FIRED = "FIRED",
  OTHER = "OTHER",
}
// display labels for Relief Reason enum
export const ReliefReasonLabel: { [key in ReliefReason]: string } = {
  [ReliefReason.VACATION]: "Vacation",
  [ReliefReason.END_OF_TOUR]: "End of Tour",
  [ReliefReason.MUTUAL_CONSENT]: "Mutual Consent",
  [ReliefReason.QUIT]: "Quit",
  [ReliefReason.RELIEF_60_DAY]: "Relief-60 Day",
  [ReliefReason.RELIEF_45_DAY]: "Relief-45 Day",
  [ReliefReason.RELIEF_COMPLETED]: "Relief - Completed",
  [ReliefReason.FIRED]: "Fired",
  [ReliefReason.OTHER]: "Other (List in Comments)",
};

export enum Publish {
  OPEN = "OPEN",
  FILLING = "FILLING",
  CREATED = "CREATED",
  CLOSED = "CLOSED",
}

export const PublishLabel: { [key in Publish]: string } = {
  [Publish.OPEN]: "Yes (Open)",
  [Publish.FILLING]: "Yes (Being Filled)",
  [Publish.CREATED]: "No (Being Created by CS)",
  [Publish.CLOSED]: "No (Closed)",
};

export enum UnLicensedJobType {
  ROTARY = "ROTARY",
  RECLAIM = "RECLAIM",
  SIXTYDAY = "SIXTYDAY",
  FORTYFIVEDAY = "FORTYFIVEDAY",
  RELIEF = "RELIEF",
  TEMPORARY = "TEMPORARY",
  PERMANENT = "PERMANENT",
  STANDBY = "STANDBY",
}
export const UnLicensedJobTypeLabel: { [key in UnLicensedJobType]: string } = {
  [UnLicensedJobType.ROTARY]: "Rotary",
  [UnLicensedJobType.RECLAIM]: "Reclaim",
  [UnLicensedJobType.SIXTYDAY]: "Relief 60 Day",
  [UnLicensedJobType.FORTYFIVEDAY]: "Relief 45 Day",
  [UnLicensedJobType.RELIEF]: "Relief",
  [UnLicensedJobType.TEMPORARY]: "Temporary",
  [UnLicensedJobType.PERMANENT]: "Permanent",
  [UnLicensedJobType.STANDBY]: "Standby",
};

export enum LicensedJobType {
  PROBATIONARY = "PROBATIONARY",
  PERMANENT = "PERMANENT",
  TEMPORARY = "TEMPORARY",
  STANDBY = "STANDBY",
}
export const LicensedJobTypeLabel: { [key in LicensedJobType]: string } = {
  [LicensedJobType.PROBATIONARY]: "Probationary",
  [LicensedJobType.PERMANENT]: "Permanent",
  [LicensedJobType.TEMPORARY]: "Temporary",
  [LicensedJobType.STANDBY]: "Standby",
};

export type jobOrderSearchParams = {
  assigneeId: string | undefined;
  vesselId: string | undefined;
  port: string | undefined;
  rating: JobRating | undefined;
  compatibleWithRating: JobRating | undefined;
  jobOrderID: string | undefined;
  fillByAfterDate: string | undefined;
  fillByBeforeDate: string | undefined;
  completed: boolean | undefined;
  notApplicable: boolean | undefined;
  completedOrNotApplicable: boolean | undefined;
  deleted: boolean | undefined;
  critical: boolean | undefined;
  newJobOrder: boolean | undefined;
  modifiedByCaptain: boolean | undefined;
  archived: boolean | undefined;
  publishStatuses: Publish[];
  smis: string | undefined;
  profileId: string | undefined;
  page: number;
  size: number;
  sortColumn: string;
  sortDirection: "asc" | "desc" | undefined;
};

